import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React, {FC, MouseEvent, useCallback} from 'react';
import {COLORS} from '../../../constants/colors';
import {RADIUS, SIZE} from '../../../constants/sizing';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {ContentPopperProps} from '../../atoms/ContentPopper/ContentPopper';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {tabFocusedStyles} from '../../atoms/TabFocused/tabFocusedStyles';
import {BannerInfoIcon, BannerInfoIconData} from './BannerInfoIcon';
import {BannerMedia, BannerMediaData} from './BannerMedia';
import {BannerProductImage, BannerProductImages} from './BannerProductImages';
import {
  PROMOTION_BANNER_CONTAINER,
  bannerWideAndUpLinaria,
} from './bannerContainer';

interface BannerBrand {
  name: string;
  logoUrl?: string;
}

export interface SponsoredBrandFollow {
  isFollowed: boolean;
  onToggle: (e: MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Header rendered on top of the banner, in one of two presentations (mirrors
 * the schema's header oneof): the "sponsored" disclosure row with the info
 * icon, or a plain one-line headline without disclosure / follow chip.
 */
export type SponsoredBrandHeader =
  | {
      type: 'sponsored';
      /** Disclosure label from BE; falls back to the translated "Sponsored". */
      label?: string;
      infoIcon: BannerInfoIconData;
    }
  | {
      type: 'headline';
      text: string;
    };

export interface SponsoredBrandData {
  media: BannerMediaData;
  backgroundColor: string;
  foregroundColor: string;
  brand?: BannerBrand;
  productImages?: BannerProductImage[];
  header?: SponsoredBrandHeader;
  follow?: SponsoredBrandFollow;
}

interface SponsoredBrandBannerProps {
  banner: SponsoredBrandData;
  onClick?: VoidFunction;
  ContentPopperLayoutComponent: ContentPopperProps['LayoutComponent'];
  className?: string;
  ['data-testid']?: string;
}

const FOLLOW_TEXT_SHADOW =
  '0 0 2px rgba(0, 0, 0, 0.23), 0 0 8px rgba(0, 0, 0, 0.25)';

export const SponsoredBrandBanner: FC<SponsoredBrandBannerProps> = ({
  banner,
  onClick,
  ContentPopperLayoutComponent,
  className,
  'data-testid': dataTestid,
}) => {
  const {t} = useTranslation();
  const {
    media,
    backgroundColor,
    foregroundColor,
    brand,
    productImages,
    header,
    follow,
  } = banner;

  const headline = header?.type === 'headline' ? header.text : undefined;
  const ariaLabel = brand
    ? `${brand.name}${media.src?.alt ? ` — ${media.src.alt}` : ''}`
    : headline ?? media.src?.alt ?? t('Sponsored');

  const handleFollowClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      follow?.onToggle(e);
    },
    [follow],
  );

  const followLabel = t('Follow');
  const following = t('Following');

  const followLabelText = follow?.isFollowed ? following : followLabel;

  return (
    <Wrapper className={className} data-testid={dataTestid}>
      <BannerButton
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        data-variant="sponsoredBrand"
      >
        <HeroSlot>
          <BannerMedia media={media} />
        </HeroSlot>
        <LeftPanel style={{['--banner-bg' as string]: backgroundColor}} />
        <Vignette />
        {productImages && productImages.length > 0 && (
          <ProductsSlot>
            <BannerProductImages
              products={productImages}
              size="sponsoredBrand"
            />
          </ProductsSlot>
        )}
        {brand?.logoUrl && (
          <LogoSlot
            role="img"
            aria-label={brand.name}
            data-testid="promotionBanner-brandLogo"
            style={{backgroundImage: `url(${brand.logoUrl})`}}
          />
        )}
        {headline !== undefined && (
          <HeadlineSlot
            data-testid="promotionBanner-headline"
            style={{color: foregroundColor}}
          >
            <HeadlineText>{headline}</HeadlineText>
          </HeadlineSlot>
        )}
      </BannerButton>
      {header?.type === 'sponsored' && (
        <TopStartGroup>
          <BannerInfoIcon
            content={header.infoIcon.content}
            foregroundColor={foregroundColor}
            ContentPopperLayoutComponent={ContentPopperLayoutComponent}
            countdown={
              header.infoIcon.countdownEndsAt
                ? {
                    endsAt: header.infoIcon.countdownEndsAt,
                    backgroundColor,
                    foregroundColor,
                  }
                : undefined
            }
            inline
          />
          <SponsoredLabel style={{color: foregroundColor}}>
            {header.label || t('Sponsored')}
          </SponsoredLabel>
        </TopStartGroup>
      )}
      {follow && (
        <FollowButton
          type="button"
          onClick={handleFollowClick}
          aria-label={followLabel}
          aria-pressed={follow.isFollowed}
          data-testid="promotionBanner-follow"
        >
          <FollowLabel style={{textShadow: FOLLOW_TEXT_SHADOW}}>
            {followLabelText}
          </FollowLabel>
          <Icon
            asset={
              follow.isFollowed
                ? GenericIconName.CheckmarkCircle
                : GenericIconName.AddCircleFill
            }
            width={SIZE.U6}
            height={SIZE.U6}
            fill={COLORS.White}
          />
        </FollowButton>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  /* Establish the query container so the banner adapts to its own width
     (full-width vs. narrow column) rather than the viewport. */
  container-type: inline-size;
  container-name: ${PROMOTION_BANNER_CONTAINER};
`;

const BannerButton = styled(UnstyledButton)`
  position: relative;
  display: block;
  width: 100%;
  height: 113px;
  border-radius: ${RADIUS.Sm};
  overflow: hidden;
  background-color: ${COLORS['Fill.Grey.Inactive']};
  -webkit-mask-image: -webkit-radial-gradient(white, black);

  &:focus-visible {
    ${tabFocusedStyles({offset: '-4px'})}
  }

  ${bannerWideAndUpLinaria(`
    height: 210px;
  `)}
`;

const HeroSlot = styled.span`
  position: absolute;
  inset-block: 0;
  inset-inline-end: 0;
  /* 251.281 / 343 ≈ 73.26% */
  width: 73.26%;
  display: block;
  overflow: hidden;

  ${bannerWideAndUpLinaria(`
    /* 467 / 1061 ≈ 44.0% */
    width: 44.0%;
  `)}
`;

const LeftPanel = styled.span`
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  /* (343 - 143) / 343 ≈ 58.31% (overlaps hero with a soft fade) */
  width: 58.31%;
  display: block;
  pointer-events: none;
  /* The fade stops use the same color at alpha 0 instead of the
     \`transparent\` keyword so the gradient interpolation stays inside the
     brand color rather than biasing toward black through the fade zone. */
  background-image: linear-gradient(
    to right,
    var(--banner-bg) 0%,
    var(--banner-bg) 52.5%,
    color-mix(in srgb, var(--banner-bg) 0%, transparent) 100%
  );

  ${bannerWideAndUpLinaria(`
    /* 820 / 1061 ≈ 77.3% (gradient frame width on desktop) */
    width: 77.3%;
    /* Figma layers two solid-then-fade gradients on desktop: stops at
       (52.7%, 95.85%) and (70%, 100%). Stacking them keeps the panel solid
       further to the right than the single mobile stop allows. */
    background-image:
      linear-gradient(
        to right,
        var(--banner-bg) 0%,
        var(--banner-bg) 52.7%,
        color-mix(in srgb, var(--banner-bg) 0%, transparent) 95.85%
      ),
      linear-gradient(
        to right,
        var(--banner-bg) 0%,
        var(--banner-bg) 70%,
        color-mix(in srgb, var(--banner-bg) 0%, transparent) 100%
      );
  `)}
`;

const Vignette = styled.span`
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  width: 58.31%;
  display: block;
  pointer-events: none;
  background-image: linear-gradient(
    to left,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.2) 42.8%,
    rgba(0, 0, 0, 0.2) 100%
  );

  ${bannerWideAndUpLinaria(`
    width: 77.3%;
  `)}
`;

const TopStartGroup = styled.div`
  position: absolute;
  top: ${SIZE.U1};
  inset-inline-start: ${SIZE.U1};
  display: inline-flex;
  align-items: center;
  gap: ${SIZE.U1};
  padding: ${SIZE.U1};
  pointer-events: auto;

  ${bannerWideAndUpLinaria(`
    top: ${SIZE.U4};
    inset-inline-start: ${SIZE.U4};
  `)}
`;

const SponsoredLabel = styled.span`
  font-size: ${FONT_SIZES.px10};
  line-height: 16px;
  text-transform: lowercase;
  pointer-events: none;

  ${bannerWideAndUpLinaria(`
    font-size: ${FONT_SIZES.px12};
    line-height: 18px;
  `)}
`;

/**
 * Plain one-line headline presentation. Mobile: vertically centered inside
 * the 40px-tall top-start band the sponsored row occupies (Figma 200:203288:
 * h-40, items-center, 4px top padding); desktop: anchored directly above the
 * products bar (Figma 2496:77290): 16px products inset + 112px tile height +
 * 8px gap. The line clamp needs \`-webkit-box\`, which cannot center
 * vertically, so the slot centers and an inner span clamps.
 */
const HeadlineSlot = styled.span`
  position: absolute;
  top: 0;
  inset-inline-start: ${SIZE.U2};
  height: 40px;
  padding-top: 4px;
  max-width: 52%;
  display: flex;
  align-items: center;
  text-align: start;
  pointer-events: none;

  ${bannerWideAndUpLinaria(`
    top: auto;
    bottom: 136px;
    inset-inline-start: ${SIZE.U4};
    height: auto;
    padding-top: 0;
    max-width: 51%;
  `)}
`;

const HeadlineText = styled.span`
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.bold};
  line-height: 18px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${bannerWideAndUpLinaria(`
    font-size: ${FONT_SIZES.px20};
    line-height: 24px;
    -webkit-line-clamp: 1;
  `)}
`;

const ProductsSlot = styled.span`
  position: absolute;
  inset-inline-start: ${SIZE.U2};
  bottom: ${SIZE.U2};
  display: inline-flex;
  pointer-events: none;

  ${bannerWideAndUpLinaria(`
    inset-inline-start: ${SIZE.U4};
    bottom: ${SIZE.U4};
  `)}
`;

const LogoSlot = styled.span`
  position: absolute;
  inset-inline-end: ${SIZE.U2};
  bottom: ${SIZE.U2};
  display: block;
  width: 130px;
  height: ${SIZE.U6};
  background-position: right center;
  background-size: contain;
  background-repeat: no-repeat;
  pointer-events: none;

  ${bannerWideAndUpLinaria(`
    inset-inline-end: ${SIZE.U4};
    bottom: ${SIZE.U4};
    width: 180px;
    height: 38px;
  `)}
`;

const FollowButton = styled(UnstyledButton)`
  position: absolute;
  top: 0;
  inset-inline-end: 0;
  display: inline-flex;
  align-items: center;
  gap: ${SIZE.U1};
  padding: ${SIZE.U2};
  color: ${COLORS.White};

  &:focus-visible {
    ${tabFocusedStyles({offset: '-2px'})}
  }

  ${bannerWideAndUpLinaria(`
    gap: ${SIZE.U2};
    padding: ${SIZE.U3};
  `)}
`;

const FollowLabel = styled.span`
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 18px;
  white-space: nowrap;

  ${bannerWideAndUpLinaria(`
    font-size: ${FONT_SIZES.px14};
    line-height: 20px;
  `)}
`;
